import { useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useAppDispatch } from 'app/hooks';
import { StepFunctionExecutionSignalType, StepFunctionExecutionStatus } from 'common/constants/admin';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { postStepFunctionExecutionTaskSignal } from 'common/services/api/StepFunctions/StepFunctions.thunks';
import { useSnackbar } from 'notistack';

import { updateRowDetailsStatus } from '../accountLinkingDashboardSlice';
import { StepFunctionExecution, StepFunctionExecutionDetails } from '../AccountLinkingDashboardTypeDefs';

import { AccountLinkingDashboardIgnoreDialog } from './AccountLinkingDashboardIgnoreDialog';

export function AccountLinkingDashboardRowActionsMenu({ row, rowDetails } : Readonly<{row: StepFunctionExecution, rowDetails: StepFunctionExecutionDetails}>) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const shouldDisableActions = usePalmButtonEvaluator([ Roles.Admin, Roles.MultiTenantRO, Roles.UserManager ]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseActions = () => {
        setIsDialogOpen(false);
        setAnchorEl(null);
    };

    const handleRetryClick = () => {
        if (rowDetails.executionId) {
            const requestParams = {
                signalType: StepFunctionExecutionSignalType.RETRY,
                executionType: row.executionType,
                executionId: rowDetails?.executionId
            };
            dispatch(postStepFunctionExecutionTaskSignal(requestParams))
                .unwrap()
                .then(() => {
                    enqueueSnackbar(`Retrying step function execution with ID: ${rowDetails.executionId?.split(':')[1]}`, { variant: 'success' });
                    dispatch(updateRowDetailsStatus([rowDetails.executionId, StepFunctionExecutionStatus.PROCESSING]));
                })
                .catch(() => {
                    enqueueSnackbar(`There was an error while retrying step function with execution ID: ${rowDetails.executionId?.split(':')[1]}`,
                        { variant: 'error' });
                });
            handleCloseActions();
        }
    };

    return (
        <>
            <IconButton
                aria-label="more"
                id="actions-button"
                aria-controls={open ? 'actions-button' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                data-testid="actions-menu"
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                id="actions-menu"
                MenuListProps={{
                    'aria-labelledby': 'actions-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseActions}
            >

                <MenuItem key={'retry'}
                    onClick={handleRetryClick}
                    disabled={shouldDisableActions}
                    data-testid="retry-button"
                >
                    Retry
                </MenuItem>
                <MenuItem key={'ignore'}
                    onClick={() => { setIsDialogOpen(true); setAnchorEl(null); }}
                    disabled={shouldDisableActions}
                    data-testid="ignore-button"
                >
                    Ignore
                </MenuItem>
            </Menu>
            <AccountLinkingDashboardIgnoreDialog
                row={row}
                rowDetails={rowDetails}
                open={isDialogOpen}
                onClose={handleCloseActions}
            />
        </>
    );
}
